/**
 * Word-pick logic for early-reader assignments: tokenize a verse into
 * display words, auto-pick a starting run (first content word, stopwords
 * skipped), and render the pick back to the text stored in
 * readingFragment. Indices always refer to tokenizeVerse() output.
 */
/** KJV-tuned function words skipped by the auto-pick */
export const STOPWORDS = new Set([
    'a', 'an', 'and', 'as', 'at', 'be', 'but', 'by', 'for', 'from',
    'had', 'hast', 'hath', 'he', 'her', 'him', 'his', 'i', 'if', 'in',
    'into', 'is', 'it', 'me', 'my', 'not', 'o', 'of', 'on', 'or',
    'our', 'shall', 'shalt', 'she', 'so', 'that', 'the', 'thee', 'their',
    'them', 'then', 'there', 'they', 'thine', 'this', 'thou', 'thy', 'to',
    'unto', 'upon', 'us', 'was', 'we', 'were', 'which', 'who', 'whom',
    'will', 'with', 'ye', 'you', 'your',
]);
/**
 * Split verse text into display words: whitespace-separated, surrounding
 * punctuation stripped, internal apostrophes and hyphens kept ("God's",
 * "Beer-sheba"). Empty tokens (bare punctuation) are dropped.
 */
export function tokenizeVerse(text) {
    return text
        .split(/\s+/)
        .map((word) => word.replace(/^[^A-Za-z']+|[^A-Za-z'-]+$/g, ''))
        .filter((word) => /[A-Za-z]/.test(word));
}
/**
 * Pick a contiguous run of `count` words starting at the first
 * non-stopword, clamped so the run fits the verse. Falls back to index 0
 * when every word is a stopword or the verse is shorter than the run.
 */
export function autoPickWords(words, count) {
    const wordCount = Math.max(1, Math.min(count, words.length || 1));
    const firstContent = words.findIndex((word) => !STOPWORDS.has(word.toLowerCase()));
    const start = firstContent === -1 ? 0 : firstContent;
    const clampedStart = Math.max(0, Math.min(start, words.length - wordCount));
    return { startWordIndex: clampedStart, wordCount };
}
/** Render a selection back to the display text stored in readingFragment */
export function renderWordSelection(words, selection) {
    return words
        .slice(selection.startWordIndex, selection.startWordIndex + selection.wordCount)
        .join(' ');
}
/** True when the selection's indices fit within the tokenized verse */
export function isSelectionValid(words, selection) {
    return (selection.startWordIndex >= 0 &&
        selection.wordCount >= 1 &&
        selection.startWordIndex + selection.wordCount <= words.length);
}
/**
 * The text a student writes: a ONE_WORD writer copies just the first word
 * of the reading fragment; everyone else copies the whole fragment.
 */
export function deriveWritingText(fragment, writingLevel) {
    if (writingLevel === 'ONE_WORD') {
        return fragment.split(/\s+/)[0] ?? fragment;
    }
    return fragment;
}
//# sourceMappingURL=word-pick.js.map